import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import Logo from '../../assets/Systemische Beratung in Dresden - Madlin Firme.png';

// ─── Konfiguration ───────────────────────────────────────────────────────────
// true  = Navbar versteckt sich beim Runterscrollen
// false = Navbar bleibt immer sichtbar
const DEFAULT_AUTO_HIDE = true;
// ─────────────────────────────────────────────────────────────────────────────

const Navbar = () => {
  const { features } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [autoHide] = useState(DEFAULT_AUTO_HIDE);
  const lastScrollY = useRef(0);

  const navLinks = useMemo(
    () => [
      { href: '/',              label: 'Start' },
      { href: '/ueber-mich',    label: 'Über mich' },
      { href: '/angebot',       label: 'Angebot' },
      { href: '/arbeitsweise',  label: 'Arbeitsweise' },
      { href: '/kontakt',       label: 'Kontakt' },
      { href: '/faq',           label: 'FAQ' },
    ],
    []
  );

  // Auto-Hide beim Scrollen
  useEffect(() => {
    if (!autoHide) {
      setIsVisible(true);
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY.current && currentScrollY > 120) {
        setIsVisible(false);
        if (isOpen) setIsOpen(false);
      } else {
        setIsVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [autoHide, isOpen]);

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: 0 }}
      animate={{ y: isVisible ? 0 : '-100%' }}
      transition={{ duration: 0.28, ease: 'easeInOut' }}
      className="fixed top-0 left-0 right-0 z-50 bg-white/70 dark:bg-[#101213]/62 backdrop-blur-md border-b border-[color:var(--color-border-subtle)] shadow-[var(--shadow-sm)]"
    >
      <div data-premium-nav="true" className="pointer-events-none absolute inset-0" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">

          {/* ── Brand ── */}
          <Link to="/" className="flex items-center gap-3 sm:gap-4 shrink-0" onClick={handleLinkClick}>
            <img
              src={Logo}
              alt="Logo"
              className="h-10 w-auto sm:h-14 flex-shrink-0"
            />
            <span className="font-serif text-sm md:text-base lg:text-xl whitespace-nowrap text-[color:var(--color-text-primary)]">
              Madlin Firme&nbsp;– malneudenken
            </span>
          </Link>

          {/* ── Desktop Navigation ── */}
          <div className="hidden md:flex items-center gap-2 lg:gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={handleLinkClick}
                className="font-medium px-2 py-1 rounded-md transition-all duration-150 text-[color:var(--color-text-secondary)] hover:bg-[color:var(--color-surface-2)] hover:text-[color:var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:rounded-[10px]"
              >
                {link.label}
              </Link>
            ))}

            <Link
              to="/kontakt"
              onClick={handleLinkClick}
              className="hidden lg:inline-flex btn btn-accent px-4 py-2.5 rounded-[var(--radius-pill)]"
            >
              Kontakt
            </Link>
          </div>

          {/* ── Mobile Menu Button ── */}
          <button
            onClick={() => setIsOpen((v) => !v)}
            className="md:hidden flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-[var(--radius-md)] bg-[color:var(--color-surface-2)] border border-[color:var(--color-border-subtle)] text-[color:var(--color-text-primary)] shadow-[var(--shadow-sm)] focus:outline-none"
            aria-label={isOpen ? 'Menü schließen' : 'Menü öffnen'}
          >
            {isOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* ── Mobile Menu ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="md:hidden overflow-hidden bg-[color:var(--color-surface-1)] border-t border-[color:var(--color-border-subtle)]"
          >
            <div className="px-4 py-5 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={handleLinkClick}
                  className="block px-4 py-3 font-medium rounded-[var(--radius-lg)] text-[color:var(--color-text-primary)] border border-transparent transition-all duration-150 hover:bg-[color:var(--color-surface-2)] hover:border-[color:var(--color-border-subtle)]"
                >
                  {link.label}
                </Link>
              ))}

              <Link
                to="/kontakt"
                onClick={handleLinkClick}
                className="btn btn-accent w-full block text-center mt-4 rounded-[var(--radius-lg)]"
              >
                Kontakt
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;